import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import { DialogClose, DialogFooter } from '@optools/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@optools/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { AssetState, AssetTier } from '@optools/services/operator-gateway/types/generated/graphql';
import {
  bulkAssetUpdateFormSchema,
  BulkAssetUpdateFormValues,
} from '@optools/tools/btc-tools/schemas/assets';
import { formatEnumValue } from '@optools/utils/format';
import { useForm } from 'react-hook-form';

export interface BulkAssetUpdatesIdleProps {
  called: boolean;
  loading: boolean;
  onSubmit: (values: BulkAssetUpdateFormValues) => void;
}

export function BulkAssetUpdatesIdle({ called, loading, onSubmit }: BulkAssetUpdatesIdleProps) {
  const form = useForm({
    defaultValues: {
      assetState: AssetState.ON,
      provider: 'ALL' as const,
      tier: 'ALL' as const,
    },
    disabled: loading,
    resolver: zodResolver(bulkAssetUpdateFormSchema),
  });

  return (
    <Form {...form}>
      <form aria-label="bulk update assets" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mb-6 grid grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="provider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provider</FormLabel>
                <Select
                  disabled={field.disabled}
                  value={field.value}
                  onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">ALL</SelectItem>
                    <SelectItem value="GCP">GCP</SelectItem>
                    <SelectItem value="AWS">AWS</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tier</FormLabel>
                <Select
                  disabled={field.disabled}
                  value={field.value}
                  onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">ALL</SelectItem>
                    <SelectItem value={AssetTier.PRIMARY}>Primary</SelectItem>
                    <SelectItem value={AssetTier.RECOVERY}>Recovery</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="assetState"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <Select
                  disabled={field.disabled}
                  value={field.value}
                  onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(AssetState)
                      .toSorted((a, b) => b.localeCompare(a))
                      .map((state) => (
                        <SelectItem key={state} value={state}>
                          {formatEnumValue(state)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} type="reset" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button disabled={loading} loading={loading} type="submit">
            {!called ? 'Submit' : 'Retry'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
